import { Dispatch, SetStateAction, use, useEffect, useMemo, useState } from 'react';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';
import useTableFiltering from 'shared/components/table/hooks/table-filtering';
import { NestedKeyof } from 'shared/components/table/utils/nested-keyof-utils';
import ObjectSort from 'shared/models/object-sort';
import Pagination from 'shared/models/pagination';

import { useReadFilterValuesMutation } from 'features/device/domain/queries/device-queries';
import LinkableDevice from 'features/device/linkable-device/domain/models/linkable-device';
import ReadLinkableDevicesCommand from 'features/device/linkable-device/domain/models/read-linkable-devices-command';
import ReadLinkableDevicesResponse from 'features/device/linkable-device/domain/models/read-linkable-devices-response';
import { useReadLinkableDevices } from 'features/device/linkable-device/domain/queries/linkable-device-queries';
import DeviceFilter from 'features/device/models/device-filter';
import DeviceFilterValue, { FilterValueType } from 'features/device/models/device-filter-value';
import { useDeviceLinkContextProvider } from 'features/device/providers/device-link-provider/device-link-provider';

type LinkableDevicesTable = {
  readLinkableDevicesData: ReadLinkableDevicesResponse | undefined;
  readLinkableDevicesIsLoading: boolean;
  readLinkableDevicesIsSuccess: boolean;
  filterValues: DeviceFilterValue[];
  currentFilters: DeviceFilter[];
  search: string;
  currentRowsPerPage: number;
  currentPage: number;
  sort: ObjectSort<LinkableDevice>;
  handleOnSearchChanged: (value: string) => void;
  setSearch: Dispatch<SetStateAction<string>>;
  handleOnOptionsSelected: (filterValueType: FilterValueType, keys: string[]) => void;
  clearFilters: () => void;
  handleOnPageChanged: (page: number) => void;
  handleOnRowsPerPageChanged: (rowsPerPage: number) => void;
  handleOnSortChanged: (property: NestedKeyof<LinkableDevice>, isAscending: boolean) => void;
};

const useLinkableDevicesTable = (): LinkableDevicesTable => {
  const { organisationUnitId, devicesToHide, handleSelectedDevicesChanged } = useDeviceLinkContextProvider();
  const {
    currentPagination: [currentPagination, setCurrentPagination],
    currentSearchQuery: [currentSearchQuery],
    currentPage,
    currentRowsPerPage,
    setCurrentPage,
    handleOnPageChanged,
    handleOnRowsPerPageChanged,
    handleOnSearchChanged,
  } = useTableFiltering();
  const { setErrorMessage } = use(ErrorMessageProviderContext);

  const [sort, setSort] = useState<ObjectSort<LinkableDevice>>({
    property: 'name',
    isAscending: true,
  });
  const [linkableDeviceFilters, setLinkableDeviceFilters] = useState<DeviceFilter[]>([]);
  const [search, setSearch] = useState<string>('');
  const [currentFilters, setCurrentFilters] = useState<DeviceFilter[]>(linkableDeviceFilters);
  const [filterValues, setFilterValues] = useState<DeviceFilterValue[]>([]);

  const {
    data: readLinkableDevicesData,
    isSuccess: readLinkableDevicesIsSuccess,
    isPending: readLinkableDevicesIsLoading,
    error: readLinkableDevicesError,
    mutate: triggerReadLinkableDevicesQuery,
  } = useReadLinkableDevices();

  const filteredData = useMemo((): ReadLinkableDevicesResponse | undefined => {
    if (!readLinkableDevicesData) {
      return undefined;
    }
    const filteredDevices =
      readLinkableDevicesData.devices.filter(
        (device) => !devicesToHide.some((deviceToHide: LinkableDevice) => deviceToHide.id === device.id),
      ) ?? [];

    return {
      ...readLinkableDevicesData,
      devices: filteredDevices,
    };
  }, [readLinkableDevicesData, devicesToHide]);

  const {
    data: readFilterValuesData,
    isSuccess: readFilterValuesIsSuccess,
    mutate: triggerReadFilterValuesQuery,
  } = useReadFilterValuesMutation();

  const handleOnSortChanged = (property: NestedKeyof<LinkableDevice>, isAscending: boolean): void => {
    setSort({
      isAscending: isAscending,
      property: property,
    });
    setCurrentPage(0);
    setCurrentPagination({
      skip: 0,
      take: currentRowsPerPage,
    });
  };

  const handleOnOptionsSelected = (filterValueType: FilterValueType, keys: string[]): void => {
    const newFilters = [...currentFilters];
    const indexOfFilterWithSameFilterValueType = newFilters.findIndex((filter) => filter.filterValueType === filterValueType);
    const deviceFilter: DeviceFilter = {
      filterValueType: filterValueType,
      values: keys,
    };

    if (indexOfFilterWithSameFilterValueType >= 0) {
      newFilters[indexOfFilterWithSameFilterValueType] = deviceFilter;
    } else {
      newFilters.push(deviceFilter);
    }

    handleOnFiltersChanged(newFilters);
  };

  const handleOnFiltersChanged = (deviceFilter: DeviceFilter[]): void => {
    setLinkableDeviceFilters(deviceFilter);
    setCurrentFilters(deviceFilter);
    setCurrentPage(0);
    handleOnPageChanged(0);
  };

  const clearFilters = (): void => {
    setLinkableDeviceFilters([]);
    setCurrentFilters([]);
    handleOnSearchChanged('');
    setSearch('');
  };

  useEffect(() => {
    const readFilterValuesCommand = {
      organisationUnitId: organisationUnitId,
    };
    triggerReadFilterValuesQuery(readFilterValuesCommand);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationUnitId]);

  useEffect(() => {
    if (readFilterValuesIsSuccess && readFilterValuesData) {
      const filteredValues = readFilterValuesData.filterValues.filter((filterValue) =>
        [FilterValueType.Type, FilterValueType.Source].includes(filterValue.filterValueType),
      );
      setFilterValues(filteredValues);
    }
  }, [readFilterValuesIsSuccess, readFilterValuesData]);

  useEffect(() => {
    if (readLinkableDevicesIsSuccess && readLinkableDevicesData) {
      handleSelectedDevicesChanged([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readLinkableDevicesIsSuccess, readLinkableDevicesData]);

  useEffect(() => {
    if (readLinkableDevicesError) {
      setErrorMessage(readLinkableDevicesError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readLinkableDevicesError]);

  useEffect(() => {
    const paginationWithHiddenDevices = {
      skip: currentPagination.skip,
      take: currentPagination.take + devicesToHide.length,
    } as Pagination;

    const readDevicesCommand = {
      organisationUnitId: organisationUnitId,
      pagination: paginationWithHiddenDevices,
      sort: sort,
      filters: currentFilters,
      searchQuery: search,
    } as ReadLinkableDevicesCommand;

    triggerReadLinkableDevicesQuery(readDevicesCommand);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, currentPagination, currentFilters, currentSearchQuery]);

  return {
    readLinkableDevicesData: filteredData,
    readLinkableDevicesIsLoading,
    readLinkableDevicesIsSuccess,
    filterValues,
    currentFilters,
    search,
    sort,
    currentPage,
    currentRowsPerPage,
    handleOnSearchChanged,
    setSearch,
    handleOnOptionsSelected,
    clearFilters,
    handleOnPageChanged,
    handleOnRowsPerPageChanged,
    handleOnSortChanged,
  };
};

export default useLinkableDevicesTable;
